/**
 * Approval Queue Page
 * 
 * Shows all decision traces pending Slack approval.
 * Features:
 * - Bulk selection and re-request
 * - Snooze functionality
 * - Show snoozed toggle
 * - Rate-limited batch operations
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MerchantHeader from '../components/MerchantHeader';
import { toast } from 'sonner';
import { fetchPendingApprovals } from '../api/approvals';
import { getSlackStatus, requestSlackApproval } from '../api/slackSettings';
import { requestSlackApprovalBatch } from '../api/slackBatch';
import { snoozeApproval, unsnoozeApproval, fetchSnoozedApprovals } from '../api/snooze';
import { ApprovalPill } from '../components/ApprovalPill';
import { 
  Card, 
  CardContent
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Clock,
  RefreshCw,
  ExternalLink,
  MessageSquare,
  Search,
  AlertTriangle,
  CheckCircle,
  Pause,
  Play,
  MoreVertical,
  ChevronDown
} from 'lucide-react';

// Default brand ID
const DEFAULT_BRAND_ID = 'demo-brand';

// Snooze duration options
const SNOOZE_OPTIONS = [
  { label: '1 hour', minutes: 60 },
  { label: '4 hours', minutes: 240 },
  { label: '1 day', minutes: 1440 },
  { label: '3 days', minutes: 4320 },
];

// Build summary for approval request
function buildSummary(trace) {
  const reasons = (trace.reason_codes || []).join(', ') || '-';
  const note = trace.rationale_text || '-';
  const obj = `${trace.object_ref?.type || 'order'}:${trace.object_ref?.id || '-'}`;
  return `*Decision:* ${trace.action_taken || 'No action'}\n*Object:* ${obj}\n*Reasons:* ${reasons}\n*Note:* ${note}`;
}

export default function ApprovalQueuePage() {
  const brandId = DEFAULT_BRAND_ID;
  
  // Main state
  const [rows, setRows] = useState([]);
  const [snoozedRows, setSnoozedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [slackStatus, setSlackStatus] = useState(null);
  
  // Bulk selection state
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  
  // Snoozed toggle
  const [showSnoozed, setShowSnoozed] = useState(false);
  
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [approvalsData, statusData, snoozedData] = await Promise.all([
        fetchPendingApprovals(brandId),
        getSlackStatus(brandId),
        showSnoozed ? fetchSnoozedApprovals(brandId) : Promise.resolve({ snoozed: [] })
      ]);
      setRows(approvalsData.pending || []);
      setSlackStatus(statusData);
      setSnoozedRows(snoozedData.snoozed || []);
      // Clear selection on refresh
      setSelected(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, showSnoozed]);
  
  useEffect(() => {
    refresh();
  }, [refresh]);
  
  // Filter pending rows
  const filtered = useMemo(() => {
    if (!filter) return rows;
    const lowerFilter = filter.toLowerCase();
    return rows.filter(r => 
      (r.decision_type || '').toLowerCase().includes(lowerFilter) ||
      (r.action_taken || '').toLowerCase().includes(lowerFilter) ||
      (r.object_ref?.id || '').toLowerCase().includes(lowerFilter)
    );
  }, [rows, filter]);
  
  // Selection helpers
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  
  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      const ids = filtered.map(r => r._id);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };
  
  const allVisibleSelected = filtered.length > 0 && filtered.every(r => selected.has(r._id));
  
  // Single re-request
  async function handleReRequest(trace) {
    if (!slackStatus?.connected) {
      toast.error('Slack not connected. Configure in Settings.');
      return;
    }
    
    if (!slackStatus.default_channel?.id) {
      toast.error('No default channel set. Configure in Settings.');
      return;
    }
    
    setBusyId(trace._id);
    try {
      await requestSlackApproval({
        teamId: slackStatus.workspace.team_id,
        brandId,
        traceId: trace._id,
        channelId: undefined,
        summary: buildSummary(trace)
      });
      toast.success('Approval request sent to Slack');
    } catch (e) {
      toast.error(e.message || 'Failed to send approval request');
    } finally {
      setBusyId(null);
    }
  }
  
  // Bulk re-request
  async function bulkReRequest(ids) {
    if (!slackStatus?.connected) {
      toast.error('Slack not connected. Configure in Settings.');
      return;
    }
    
    if (!slackStatus.default_channel?.id) {
      toast.error('No default channel set. Configure in Settings.');
      return;
    }
    
    setBulkBusy(true);
    try {
      const res = await requestSlackApprovalBatch({
        teamId: slackStatus.workspace.team_id,
        brandId,
        traceIds: ids,
        maxPerMinute: 15
      });
      
      toast.success(`Sent ${res.sent}/${ids.length} approval requests`);
      setSelected(new Set());
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Failed to send batch requests');
    } finally {
      setBulkBusy(false);
    }
  }
  
  // Snooze handler
  async function handleSnooze(traceId, minutes) {
    setBusyId(traceId);
    try {
      await snoozeApproval({
        brandId,
        traceId,
        minutes,
        actor: 'user'
      });
      toast.success(`Snoozed for ${minutes >= 1440 ? `${minutes / 1440} day(s)` : `${minutes / 60} hour(s)`}`);
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Failed to snooze');
    } finally {
      setBusyId(null);
    }
  }
  
  // Unsnooze handler
  async function handleUnsnooze(traceId) {
    setBusyId(traceId);
    try {
      await unsnoozeApproval({
        brandId,
        traceId,
        actor: 'user'
      });
      toast.success('Unsnoozed successfully');
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Failed to unsnooze');
    } finally {
      setBusyId(null);
    }
  }
  
  return (
    <div className="min-h-screen bg-background" data-testid="approval-queue-page">
      <MerchantHeader />
      
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Clock className="h-8 w-8 text-primary" />
              Slack Approval Queue
            </h1>
            <p className="text-muted-foreground mt-1">
              Decision traces waiting for approval in Slack
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {slackStatus?.connected ? (
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                <CheckCircle className="h-3 w-3 mr-1" />
                Slack Connected
              </Badge>
            ) : (
              <Link to="/dashboard/settings/integrations/slack">
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 cursor-pointer">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Connect Slack
                </Badge>
              </Link>
            )}
            <Button 
              variant="outline" 
              size="icon"
              onClick={refresh}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Filter Bar */}
        <Card className="mb-4 bg-card border-border">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Filter by type, action, or object..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="pl-10"
                  data-testid="filter-input"
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    id="show-snoozed" 
                    checked={showSnoozed}
                    onCheckedChange={setShowSnoozed}
                  />
                  <Label htmlFor="show-snoozed" className="text-sm text-muted-foreground">
                    Show snoozed
                  </Label>
                </div>
                <div className="text-sm text-muted-foreground whitespace-nowrap">
                  {filtered.length} pending
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Bulk Action Bar */}
        {filtered.length > 0 && (
          <Card className="mb-4 bg-card border-border">
            <CardContent className="p-3">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleAllVisible}
                  disabled={bulkBusy}
                  data-testid="select-all-btn"
                >
                  {allVisibleSelected ? 'Unselect visible' : 'Select visible'}
                </Button>
                
                <div className="text-sm text-muted-foreground">
                  Selected: <span className="font-medium">{selected.size}</span>
                </div>
                
                <div className="ml-auto flex gap-2">
                  <Button
                    size="sm"
                    disabled={bulkBusy || selected.size === 0 || !slackStatus?.connected}
                    onClick={() => bulkReRequest(Array.from(selected))}
                    data-testid="bulk-rerequest-btn"
                  >
                    {bulkBusy ? (
                      <>
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Re-request selected ({selected.size})
                      </>
                    )}
                  </Button>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={bulkBusy || filtered.length === 0 || !slackStatus?.connected}
                      >
                        All filtered
                        <ChevronDown className="h-3 w-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onClick={() => bulkReRequest(filtered.map(r => r._id))}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Re-request all ({filtered.length})
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {bulkBusy && (
                <p className="text-xs text-muted-foreground mt-2">
                  Sending at 15/min to avoid Slack rate limits...
                </p>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Main Content */}
        {loading ? (
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="bg-card border-destructive/50">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Error Loading Data</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={refresh} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : filtered.length === 0 && snoozedRows.length === 0 ? (
          <Card className="bg-card border-border border-dashed">
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Pending Approvals</h3>
              <p className="text-muted-foreground">
                All decision traces are either approved or do not require Slack approval.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Pending Table */}
            {filtered.length > 0 && (
              <Card className="bg-card border-border overflow-hidden mb-6">
                <Table data-testid="approvals-table">
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allVisibleSelected}
                          onCheckedChange={toggleAllVisible}
                          aria-label="Select all"
                        />
                      </TableHead>
                      <TableHead>Decision</TableHead>
                      <TableHead>Object</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((row) => {
                      const obj = `${row.object_ref?.type || 'order'}:${row.object_ref?.id || '-'}`;
                      const isBusy = busyId === row._id;
                      
                      return (
                        <TableRow key={row._id} data-testid={`row-${row._id}`}>
                          <TableCell>
                            <Checkbox
                              checked={selected.has(row._id)}
                              onCheckedChange={() => toggleOne(row._id)}
                              aria-label={`Select ${row._id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-sm">
                              {row.action_taken || 'No action'}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {row.decision_type?.replace(/_/g, ' ')}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {obj}
                          </TableCell>
                          <TableCell>
                            <ApprovalPill state="pending" />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {row.created_at 
                              ? new Date(row.created_at).toLocaleString()
                              : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="default"
                                disabled={isBusy || !slackStatus?.connected}
                                onClick={() => handleReRequest(row)}
                                data-testid={`rerequest-${row._id}`}
                              >
                                {isBusy ? (
                                  <>
                                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                    Sending...
                                  </>
                                ) : (
                                  <>
                                    <MessageSquare className="h-3 w-3 mr-1" />
                                    Re-request
                                  </>
                                )}
                              </Button>
                              
                              {/* Snooze dropdown */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={isBusy}
                                  >
                                    <Pause className="h-3 w-3 mr-1" />
                                    Snooze
                                    <ChevronDown className="h-3 w-3 ml-1" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                  {SNOOZE_OPTIONS.map((opt) => (
                                    <DropdownMenuItem
                                      key={opt.minutes}
                                      onClick={() => handleSnooze(row._id, opt.minutes)}
                                    >
                                      <Pause className="h-4 w-4 mr-2" />
                                      {opt.label}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                              
                              <Link to={`/dashboard/exception-radar?trace=${encodeURIComponent(row._id)}`}>
                                <Button size="sm" variant="outline">
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Open
                                </Button>
                              </Link>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            )}
            
            {/* Snoozed Section */}
            {showSnoozed && snoozedRows.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Pause className="h-5 w-5 text-muted-foreground" />
                  Snoozed ({snoozedRows.length})
                </h3>
                <Card className="bg-card border-border border-dashed overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/20">
                        <TableHead>Decision</TableHead>
                        <TableHead>Object</TableHead>
                        <TableHead>Snoozed Until</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {snoozedRows.map((row) => {
                        const obj = `${row.object_ref?.type || 'order'}:${row.object_ref?.id || '-'}`;
                        const snoozedUntil = row.metadata?.snoozed_until;
                        const isBusy = busyId === row._id;
                        
                        return (
                          <TableRow key={row._id} className="opacity-70">
                            <TableCell>
                              <div className="font-medium text-sm">
                                {row.action_taken || 'No action'}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {row.decision_type?.replace(/_/g, ' ')}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {obj}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {snoozedUntil 
                                ? new Date(snoozedUntil).toLocaleString()
                                : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={isBusy}
                                onClick={() => handleUnsnooze(row._id)}
                              >
                                {isBusy ? (
                                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <Play className="h-3 w-3 mr-1" />
                                )}
                                Unsnooze
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}
          </>
        )}
        
        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          {slackStatus?.default_channel?.name ? (
            <p>
              Approvals are sent to <strong>#{slackStatus.default_channel.name}</strong>.{' '}
              <Link to="/dashboard/settings/integrations/slack" className="text-primary hover:underline">
                Change in Settings
              </Link>
            </p>
          ) : slackStatus?.connected ? (
            <p>
              <Link to="/dashboard/settings/integrations/slack" className="text-primary hover:underline">
                Set a default approval channel
              </Link>
              {' '}to enable one-click re-requests.
            </p>
          ) : (
            <p>
              <Link to="/dashboard/settings/integrations/slack" className="text-primary hover:underline">
                Connect Slack
              </Link>
              {' '}to enable approval workflows.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
